/**
 * Shared Mapbox style config for both project maps (MapLanding's interactive
 * pin-placement map and ProjectDashboard's mini preview) - same style family,
 * same brand theming, so the two stay visually consistent.
 */

// 'satellite' -> imagery, 'roadmap' -> Mapbox's Standard style (3D buildings,
// terrain-ready, and the only style family that supports the theme/color
// config below). Standard Satellite supports lightPreset + label toggles but
// not theme/color - that's a Mapbox platform limitation, not a choice here.
export const MAP_STYLES = {
  satellite: 'mapbox://styles/mapbox/standard-satellite',
  roadmap: 'mapbox://styles/mapbox/standard',
};

/**
 * Applies CrewChain's brand theming + 3D terrain to a Mapbox Standard/Standard
 * Satellite map instance. Call on the map's initial "load" AND on every
 * subsequent "style.load" (style/mapType switches wipe custom sources/config,
 * and mapType often loads asynchronously from a saved preference after mount).
 *
 * @param {import('mapbox-gl').Map} map
 * @param {{ hideLabels?: boolean }} [options] - hideLabels: true for the compact
 *   dashboard preview (always label-free); false/omitted for the full map, which
 *   wants labels visible in satellite mode for way-finding while placing a pin.
 */
export function applyMapboxStyleExtras(map, { hideLabels = false } = {}) {
  if (!map.getSource('mapbox-dem')) {
    map.addSource('mapbox-dem', {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 512,
      maxzoom: 14,
    });
  }
  map.setTerrain({ source: 'mapbox-dem', exaggeration: 1 });

  try {
    map.setConfigProperty('basemap', 'lightPreset', 'day');
    map.setConfigProperty('basemap', 'theme', 'faded'); // no-op on Standard Satellite
    map.setConfigProperty('basemap', 'colorBuildingHighlight', '#0e172a'); // CrewChain navy
    map.setConfigProperty('basemap', 'colorBuildingSelect', '#0e172a');
    if (hideLabels) {
      map.setConfigProperty('basemap', 'showPlaceLabels', false);
      map.setConfigProperty('basemap', 'showPointOfInterestLabels', false);
      map.setConfigProperty('basemap', 'showRoadLabels', false);
      map.setConfigProperty('basemap', 'showTransitLabels', false);
    }
  } catch (e) {
    // Style doesn't support config properties - nothing to do
  }
}
